import type {
  ExtensionDraftSummary,
  ExtensionPackageCreated,
  ExtensionPackageSummary,
} from "@api-client";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Dialog } from "@sylviq/ui-react-web/components/ui/dialog";
import { EmptyState } from "@sylviq/ui-react-web/components/ui/empty-state";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { type FormEvent, type ReactNode, useState } from "react";
import {
  type BuildDraftValidationReport,
  createBuildDraft,
  createBuildPackage,
  putBuildDraftFile,
  resolveExtensionDocsUrl,
  validateBuildDraft,
} from "@/lib/api";

/** Canonical product app IDs from @sylviq/extension-sdk generated catalogue types. */
const PRODUCT_APP_IDS = [
  "niche-accounting",
  "sylviq-inventory",
  "sylviq-payroll",
  "sylviq-purchasing",
  "sylviq-sales",
] as const;

const EXTENSION_KEY_PATTERN = /^[a-z0-9-]+$/;
const DISPLAY_NAME_MAX_CHARS = 80;
const EXTENSION_KEY_MAX_CHARS = 64;

type CuratedListState<T> =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "loaded"; items: T[] };

type CreateFieldErrors = {
  displayName?: string;
  key?: string;
  apps?: string;
};

type CreateOutcome =
  | {
      status: "created";
      extensionPackage: ExtensionPackageCreated;
      draftId: string;
      report: BuildDraftValidationReport;
    }
  | { status: "failed"; message: string; retryable: boolean };

function errorStatus(error: unknown): number | null {
  if (typeof error !== "object" || error === null || !("status" in error)) return null;
  return typeof error.status === "number" ? error.status : null;
}

/** Minimal canonical manifest: key, api_version, kind, explicit empty grants, target apps. */
export function canonicalExtensionManifest(
  publisher: string,
  key: string,
  targetApps: string[],
): string {
  return `${JSON.stringify(
    {
      key: { publisher, key },
      api_version: { major: 1, minor: 0 },
      kind: "organization",
      target_apps: targetApps,
      requested_capabilities: [],
    },
    null,
    2,
  )}\n`;
}

export function ExtensionsCuratedPanel({
  organizationId,
  publisher,
  packages,
  drafts,
  onCreated,
  children,
}: {
  organizationId: string;
  publisher: string;
  packages: CuratedListState<ExtensionPackageSummary>;
  drafts: CuratedListState<ExtensionDraftSummary>;
  onCreated: () => void;
  children: ReactNode;
}) {
  const [createOpen, setCreateOpen] = useState(false);
  const [displayName, setDisplayName] = useState("");
  const [extensionKey, setExtensionKey] = useState("");
  const [targetApps, setTargetApps] = useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<CreateFieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState<CreateOutcome | null>(null);

  const bothListsEmpty =
    packages.status === "loaded" &&
    packages.items.length === 0 &&
    drafts.status === "loaded" &&
    drafts.items.length === 0;

  const openCreateDialog = () => {
    if (submitting) return;
    setDisplayName("");
    setExtensionKey("");
    setTargetApps([]);
    setFieldErrors({});
    setOutcome(null);
    setCreateOpen(true);
  };

  const toggleTargetApp = (app: string) => {
    setTargetApps((current) =>
      current.includes(app)
        ? current.filter((candidate) => candidate !== app)
        : [...current, app],
    );
    setFieldErrors((current) => ({ ...current, apps: undefined }));
  };

  const validateCreateFields = (): CreateFieldErrors => {
    const errors: CreateFieldErrors = {};
    const name = displayName.trim();
    if (!name) {
      errors.displayName = "Enter a display name.";
    } else if (name.length > DISPLAY_NAME_MAX_CHARS) {
      errors.displayName = `Keep the display name to ${DISPLAY_NAME_MAX_CHARS} characters or less.`;
    }
    const key = extensionKey.trim();
    if (!key) {
      errors.key = "Enter an extension key.";
    } else if (key.length > EXTENSION_KEY_MAX_CHARS) {
      errors.key = `Keep the key to ${EXTENSION_KEY_MAX_CHARS} characters or less.`;
    } else if (!EXTENSION_KEY_PATTERN.test(key)) {
      errors.key = "Use lowercase letters, digits, and hyphens only.";
    }
    if (targetApps.length === 0) {
      errors.apps = "Select at least one supported app.";
    }
    return errors;
  };

  const submitCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submitting || outcome?.status === "created") return;
    if (!publisher) {
      setOutcome({
        status: "failed",
        message: "This organization has no publisher identity. Reload the page and try again.",
        retryable: false,
      });
      return;
    }

    const errors = validateCreateFields();
    setFieldErrors(errors);
    if (Object.values(errors).some(Boolean)) return;

    const key = extensionKey.trim();
    const selectedApps = PRODUCT_APP_IDS.filter((app) => targetApps.includes(app));
    let step: "package" | "draft" | "manifest" | "validate" = "package";
    setSubmitting(true);
    setOutcome(null);
    try {
      const createdPackage = await createBuildPackage(organizationId, {
        organization_id: organizationId,
        publisher,
        key,
      });
      step = "draft";
      const createdDraft = await createBuildDraft(organizationId, {
        organization_id: organizationId,
        package_id: createdPackage.id,
      });
      step = "manifest";
      await putBuildDraftFile(organizationId, createdDraft.id, createdDraft.revision, {
        organization_id: organizationId,
        path: "manifest.json",
        source: canonicalExtensionManifest(publisher, key, [...selectedApps]),
      });
      step = "validate";
      const report = await validateBuildDraft(organizationId, createdDraft.id);
      setOutcome({
        status: "created",
        extensionPackage: createdPackage,
        draftId: createdDraft.id,
        report,
      });
      onCreated();
    } catch (error) {
      if (step === "package") {
        setOutcome({
          status: "failed",
          message: errorStatus(error) === 409
            ? "An extension with this key already exists for this publisher. Choose a different key."
            : "The extension package could not be created. Nothing was created; please try again.",
          retryable: true,
        });
      } else {
        onCreated();
        setOutcome({
          status: "failed",
          message: step === "draft"
            ? "The package was created, but its draft could not be created. Refresh the list, open the package, and create the draft again."
            : step === "manifest"
              ? "The draft was created, but its manifest file could not be saved. Open the draft and add manifest.json before validating."
              : "The draft and manifest were created, but validation could not run. Open the draft and validate it later.",
          retryable: false,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section aria-labelledby="packages-heading" className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h2 id="packages-heading" className="text-lg font-semibold">
            Packages
          </h2>
          <p className="text-sm text-[var(--muted-foreground)]">
            Organization packages plus this organization’s drafts. Creating an extension starts a draft; it is not installed, granted, or executed.
          </p>
        </div>
        <Button aria-label="Create extension" disabled={submitting} onClick={openCreateDialog}>
          + Create Extension
        </Button>
      </div>

      {packages.status === "loading" ? (
        <p role="status" className="text-sm text-[var(--muted-foreground)]">
          Loading packages...
        </p>
      ) : null}
      {packages.status === "error" ? (
        <p role="alert" className="text-sm text-[var(--danger)]">
          {packages.message}
        </p>
      ) : null}

      {bothListsEmpty ? (
        <EmptyState
          title="No extensions yet"
          reason="Create an extension to start an organization package and this organization’s first draft."
          action={
            <Button aria-label="Create extension" onClick={openCreateDialog}>
              + Create Extension
            </Button>
          }
        />
      ) : (
        children
      )}

      <Dialog
        description="Creates an organization package and this organization’s first draft. Nothing is installed, granted, or executed."
        onOpenChange={(open) => {
          if (!open && !submitting) setCreateOpen(false);
        }}
        open={createOpen}
        title="Create extension"
      >
        {outcome?.status === "created" ? (
          <div className="space-y-4">
            <p className="text-sm font-medium">
              Created “{displayName.trim()}” ({outcome.extensionPackage.publisher}/{outcome.extensionPackage.key}).
            </p>
            <p className="text-sm text-[var(--muted-foreground)]">
              Draft <code>{outcome.draftId}</code> was created in this organization with a canonical manifest. Creating a draft never installs, grants, or runs the extension.
            </p>
            <section aria-label="Initial validation report" className="space-y-2 text-sm">
              <h3 className="font-medium">Initial validation</h3>
              {outcome.report.valid ? (
                <p role="status">The server reported no validation problems.</p>
              ) : (
                <>
                  <p role="status">
                    The server reported {outcome.report.problems.length} validation problem{outcome.report.problems.length === 1 ? "" : "s"} for this draft:
                  </p>
                  <ul className="space-y-2">
                    {outcome.report.problems.map((problem, index) => {
                      const documentationUrl = resolveExtensionDocsUrl(problem.docs_url);
                      return (
                        <li
                          className="rounded-md border border-[var(--border)] p-3"
                          key={`${problem.code}:${problem.path}:${problem.line}:${index}`}
                        >
                          <p>
                            <code>{problem.code}</code> · <code>{problem.path}</code> · line {problem.line}
                          </p>
                          <p className="mt-1">{problem.message}</p>
                          {documentationUrl ? (
                            <a
                              aria-label="Validation documentation"
                              className="mt-2 inline-block underline"
                              href={documentationUrl}
                              rel="noreferrer"
                              target="_blank"
                            >
                              Documentation
                            </a>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </section>
            <div className="flex justify-end">
              <Button onClick={() => setCreateOpen(false)}>Close</Button>
            </div>
          </div>
        ) : (
          <form
            aria-label="Create extension"
            className="grid gap-4"
            noValidate
            onSubmit={(event) => void submitCreate(event)}
          >
            <p className="text-sm">
              Publisher: <code>{publisher || "Unavailable"}</code>
            </p>
            <label className="grid gap-1.5 text-sm font-medium">
              Display name
              <Input
                aria-label="Extension display name"
                autoComplete="off"
                maxLength={DISPLAY_NAME_MAX_CHARS}
                onChange={(event) => {
                  setDisplayName(event.target.value);
                  setFieldErrors((current) => ({ ...current, displayName: undefined }));
                }}
                required
                value={displayName}
              />
            </label>
            {fieldErrors.displayName ? (
              <p role="alert" className="text-sm text-[var(--danger)]">
                {fieldErrors.displayName}
              </p>
            ) : null}
            <label className="grid gap-1.5 text-sm font-medium">
              Extension key
              <Input
                aria-label="Extension key"
                autoComplete="off"
                maxLength={EXTENSION_KEY_MAX_CHARS}
                onChange={(event) => {
                  setExtensionKey(event.target.value);
                  setFieldErrors((current) => ({ ...current, key: undefined }));
                }}
                required
                value={extensionKey}
              />
            </label>
            <p className="text-xs text-[var(--muted-foreground)]">
              Lowercase letters, digits, and hyphens. This draft’s extension identity is <code>{publisher || "…"}/{extensionKey.trim() || "…"}</code>.
            </p>
            {fieldErrors.key ? (
              <p role="alert" className="text-sm text-[var(--danger)]">
                {fieldErrors.key}
              </p>
            ) : null}
            <fieldset className="grid gap-2">
              <legend className="text-sm font-medium">Supported apps</legend>
              {PRODUCT_APP_IDS.map((app) => (
                <label className="flex items-center gap-2 text-sm" key={app}>
                  <Input
                    aria-label={`Supported app ${app}`}
                    checked={targetApps.includes(app)}
                    className="size-4"
                    onChange={() => toggleTargetApp(app)}
                    type="checkbox"
                  />
                  <code>{app}</code>
                </label>
              ))}
              {fieldErrors.apps ? (
                <p role="alert" className="text-sm text-[var(--danger)]">
                  {fieldErrors.apps}
                </p>
              ) : null}
            </fieldset>
            {outcome?.status === "failed" ? (
              <p role="alert" className="text-sm text-[var(--danger)]">
                {outcome.message}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button
                disabled={submitting}
                onClick={() => setCreateOpen(false)}
                type="button"
                variant="outline"
              >
                Cancel
              </Button>
              {outcome?.status === "failed" && !outcome.retryable ? null : (
                <Button aria-label="Create extension package" disabled={submitting} type="submit">
                  {submitting ? "Creating extension..." : "Create extension"}
                </Button>
              )}
            </div>
          </form>
        )}
      </Dialog>
    </section>
  );
}
